<template>
  <div class="mobile-support-container">
    <!-- Header with back buttons -->
    <div class="mobile-header">
      <router-link to="/" class="mobile-back-btn">
        <i class="fa-solid fa-arrow-left"></i> Back
      </router-link>
      <h1 class="mobile-title">Customer Support</h1>
      <router-link to="/appointments" class="mobile-exit-btn">
        Exit <i class="fa-solid fa-arrow-right-to-bracket"></i>
      </router-link>
    </div>

    <!-- Chatbot Container -->
    <div class="mobile-chatbot-container">
      <div class="mobile-chatbot-header">
        <h2>FAQ Chatbot</h2>
      </div>

      <!-- FAQ Categories -->
      <div class="mobile-categories">
        <div
          v-for="(category, index) in mobileCategories"
          :key="'cat-'+index"
          class="mobile-category"
          @click="toggleCategory(category.name)"
        >
          {{ category.name }}
          <div 
            v-if="activeCategory === category.name"
            class="mobile-faq-dropdown"
          >
            <div
              v-for="(faq, faqIndex) in category.faqs"
              :key="'faq-'+faqIndex"
              class="mobile-faq-item"
              @click.stop="selectQuestion(faq)"
            >
              {{ faq.question }}
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Messages -->
      <div class="mobile-chat-body">
        <div
          v-for="(message, index) in messages"
          :key="'msg-'+index"
          class="mobile-message"
          :class="message.type"
        >
          {{ message.text }}
        </div>
        <div v-if="isWaitingForAIResponse" class="mobile-message bot">
          <i class="fa-solid fa-spinner fa-spin"></i> Thinking...
        </div>
      </div>

      <!-- Input Area -->
      <div class="mobile-input-area">
        <input
          v-model="userInput"
          @keyup.enter="sendMessage"
          placeholder="Type your question..."
          class="mobile-chat-input"
          :disabled="isWaitingForAIResponse"
        />
        <button @click="sendMessage" class="mobile-send-btn" :disabled="isWaitingForAIResponse">
          <i class="fa-solid fa-paper-plane"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "MobileCustomerSupport",
  data() {
    return {
      userInput: "",
      messages: [
        { type: "bot", text: "Hello! How can I help you today?" }
      ],
      activeCategory: null,
      mobileCategories: [
        {
          name: "Hours",
          faqs: [
            { question: "When are you open", answer: "We're open Mon-Fri, 9am-6pm." },
            { question: "Weekend hours", answer: "We're closed on weekends." }
          ]
        },
        {
          name: "Booking",
          faqs: [
            { question: "How to book", answer: "Use our app or the kiosk." },
            { question: "Cancel appointment", answer: "Yes, via app or call us." }
          ]
        },
        {
          name: "Check-In",
          faqs: [
            { question: "What is kiosk for", answer: "For quick, line-free check-in." },
            { question: "Walk-ins allowed", answer: "Yes, based on availability." }
          ]
        },
        {
          name: "Policies",
          faqs: [
            { question: "Kids/pets policy", answer: "Children and service animals only." },
            { question: "Payment methods", answer: "Cash, cards, mobile pay." }
          ]
        }
      ],
      openAIKey: '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', // Replace with your actual OpenAI API key
      openAIModel: 'gpt-4-1106-preview', // You can change this to other models like 'gpt-4'
      systemPrompt: `You are a helpful customer support assistant for a business. 
        Here are some FAQs you can reference:
        
        Hours:
        - We're open Mon-Fri, 9am-6pm
        - Closed on weekends
        
        Booking:
        - Use our app or the kiosk to book
        - You can cancel via app or by calling us
        
        Check-In:
        - Kiosk is for quick, line-free check-in
        - Walk-ins are allowed based on availability
        
        Policies:
        - Only children and service animals allowed
        - We accept cash, cards, and mobile pay
        
        Be friendly, concise, and professional. If the question is about these topics, 
        use the FAQ information first. For other questions, provide helpful answers.`,
      isWaitingForAIResponse: false
    };
  },
  methods: {
    async sendMessage() {
      if (!this.userInput.trim() || this.isWaitingForAIResponse) return;
      
      // Add user message
      const userMessage = this.userInput;
      this.messages.push({
        type: "user",
        text: userMessage
      });
      this.userInput = "";
      
      // First try to find answer in FAQs
      const faqAnswer = this.findAnswer(userMessage);
      if (faqAnswer) {
        this.messages.push({
          type: "bot",
          text: faqAnswer
        });
        this.scrollToBottom();
        return;
      }
      
      // If no FAQ answer, call OpenAI
      this.isWaitingForAIResponse = true;
      try {
        const aiResponse = await this.getAIResponse(userMessage);
        this.messages.push({
          type: "bot",
          text: aiResponse
        });
      } catch (error) {
        console.error("Error calling OpenAI:", error);
        this.messages.push({
          type: "bot",
          text: "I'm having trouble connecting to the support system. Please try again later or contact us directly."
        });
      } finally {
        this.isWaitingForAIResponse = false;
        this.scrollToBottom();
      }
    },
    
    async getAIResponse(userMessage) {
  const messages = [
    { role: "system", content: this.systemPrompt },
    { role: "user", content: userMessage }
  ];
  
  // Include conversation history
  if (this.messages.length > 1) {
    this.messages.slice(1).forEach(msg => {
      messages.push({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.text
      });
    });
  }
  
  try {
    const response = await axios.post(
      `${process.env.VUE_APP_API_URL}openai/chat`,
      {
        messages,
        model: this.openAIModel,
        temperature: 0.7,
        max_tokens: 150
      }
    );
    
    return response.data.choices[0].message.content.trim();
  } catch (error) {
    console.error("Error calling OpenAI:", error);
    if (error.response && error.response.status === 429) {
      return "I'm getting too many requests right now. Please wait a moment and try again.";
    }
    return "I'm having trouble connecting to the support system. Please try again later or contact us directly.";
  }
},
    
    findAnswer(question) {
      const q = question.toLowerCase();
      for (const category of this.mobileCategories) {
        for (const faq of category.faqs) {
          if (faq.question.toLowerCase().includes(q) || 
              q.includes(faq.question.toLowerCase())) {
            return faq.answer;
          }
        }
      }
      return null;
    },
    
    toggleCategory(name) {
      this.activeCategory = this.activeCategory === name ? null : name;
    },
    
    selectQuestion(faq) {
      this.messages.push(
        { type: "user", text: faq.question },
        { type: "bot", text: faq.answer }
      );
      this.activeCategory = null;
      this.scrollToBottom();
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.mobile-chat-body');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }
};
</script>

<style scoped>
/* Base Styles */
.mobile-support-container {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: #f5f7fa;
}

/* Header Styles */
.mobile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: #1e3a8a;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.mobile-title {
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
  text-align: center;
  flex-grow: 1;
}

.mobile-back-btn, .mobile-exit-btn {
  color: white;
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

.mobile-back-btn {
  margin-right: auto;
}

.mobile-exit-btn {
  margin-left: auto;
}

/* Chatbot Container */
.mobile-chatbot-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: white;
  margin: 10px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
}

.mobile-chatbot-header {
  padding: 15px;
  background-color: #2563eb;
  color: white;
  text-align: center;
}

.mobile-chatbot-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
}

/* Categories */
.mobile-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background-color: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.mobile-category {
  background-color: #1e40af;
  color: white;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  transition: background-color 0.2s;
}

.mobile-category:hover {
  background-color: #1e3a8a;
}

.mobile-faq-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 280px;
  max-width: 90vw;
  margin-top: 5px;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  z-index: 20;
  max-height: 300px;
  overflow-y: auto;
}

.mobile-faq-item {
  padding: 10px 15px;
  font-size: 0.85rem;
  color: #1e3a8a;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background-color 0.2s;
}

.mobile-faq-item:last-child {
  border-bottom: none;
}

.mobile-faq-item:hover {
  background-color: #f8fafc;
}

/* Chat Body */
.mobile-chat-body {
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: #f8fafc;
}

.mobile-message {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 18px;
  font-size: 0.9rem;
  line-height: 1.4;
}

.mobile-message.user {
  align-self: flex-end;
  background-color: #2563eb;
  color: white;
  border-bottom-right-radius: 4px;
}

.mobile-message.bot {
  align-self: flex-start;
  background-color: #e0e7ff;
  color: #1e3a8a;
  border-bottom-left-radius: 4px;
}

/* Input Area */
.mobile-input-area {
  display: flex;
  padding: 12px;
  background-color: white;
  border-top: 1px solid #e2e8f0;
  gap: 8px;
}

.mobile-chat-input {
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  font-size: 0.9rem;
  outline: none;
}

.mobile-chat-input:focus {
  border-color: #2563eb;
}

.mobile-chat-input:disabled {
  background-color: #f1f5f9;
  cursor: not-allowed;
}

.mobile-send-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #2563eb;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.mobile-send-btn:hover:not(:disabled) {
  background-color: #1e40af;
}

.mobile-send-btn:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

/* Responsive Adjustments */
@media (max-width: 480px) {
  .mobile-title {
    font-size: 1.1rem;
  }
  
  .mobile-category {
    font-size: 0.75rem;
    padding: 6px 10px;
  }
  
  .mobile-message {
    max-width: 90%;
    font-size: 0.85rem;
  }
  
  .mobile-input-area {
    padding: 10px;
  }
  
  .mobile-chat-input {
    padding: 8px 12px;
  }
}

@media (max-width: 360px) {
  .mobile-header {
    padding: 10px;
  }
  
  .mobile-title {
    font-size: 1rem;
  }
  
  .mobile-back-btn, .mobile-exit-btn {
    font-size: 0.8rem;
  }
  
  .mobile-chatbot-header h2 {
    font-size: 1rem;
  }
  
  .mobile-categories {
    gap: 6px;
    padding: 8px;
  }
  
  .mobile-category {
    font-size: 0.7rem;
    padding: 5px 8px;
    border-radius: 16px;
  }
}
</style>